import { useState } from "react";
import { Bug, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";

type Props = {
  label?: string;
  variant?: "default" | "outline" | "secondary" | "ghost";
  size?: "default" | "sm";
  errorMessage?: string;
  pagePath?: string;
};

export function CrewAppIssueReporter({
  label = "Report app issue",
  variant = "outline",
  size = "sm",
  errorMessage,
  pagePath,
}: Props) {
  const { user } = useAuth();
  const { isAdmin, isForeman, isWorker } = useUserRole();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const role = isAdmin ? "admin" : isForeman ? "foreman" : isWorker ? "worker" : "crew";

  const submit = async () => {
    if (!user) {
      toast.error("You need to be signed in to report an issue.");
      return;
    }
    if (message.trim().length < 8 && !errorMessage) {
      toast.error("Add a short note about what happened.");
      return;
    }

    setSaving(true);
    try {
      const { error } = await (supabase as any).from("crew_app_issue_reports").insert({
        user_id: user.id,
        role,
        page_path: pagePath || window.location.pathname,
        message: message.trim() || "App error shown on screen.",
        error_message: errorMessage || null,
        user_agent: navigator.userAgent,
      });
      if (error) throw error;
      toast.success("Issue sent to the office.");
      setOpen(false);
      setMessage("");
    } catch (e: any) {
      toast.error(e.message || "Could not send issue report.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button type="button" variant={variant} size={size} onClick={() => setOpen(true)}>
        <Bug className="h-4 w-4 mr-2" />
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report an app issue</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="crew-issue-message">What happened?</Label>
              <Textarea
                id="crew-issue-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Example: I tapped the job photos button and got a blank screen."
                rows={4}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              This automatically includes the current page and device/browser info so the office can troubleshoot it.
            </p>
            {errorMessage && (
              <p className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
                Error shown: {errorMessage}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
